/**
 * Easter gift tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterGiftTagsPack: ToilePack = {
    slug: 'easter-gift-tags',
    category: 'season',
    name: 'Easter gift tags',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'egg', label: 'Egg tags', subject: 'A sheet of egg-shaped gift tags each with a patterned band and a blank centre, string holes, dashed cut lines'},
        {key: 'bunny', label: 'From the bunny', subject: 'A sheet of rectangular tags each lettered "From the Easter Bunny" with a ruled line for a name, dashed cut lines'},
        {key: 'carrot', label: 'Carrot tags', subject: 'A sheet of tiny carrot-shaped tags with string holes, dashed cut lines, no lettering'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['with a name blank', 'as a set of six', 'sized for a wine bottle'],
    directionHint: 'try: with a name blank',
    seo: {
        title: 'Easter gift tags: egg, bunny and carrot',
        metaDescription: "Nine Easter gift tags — an egg-shaped tag, a 'from the bunny' tag, a tiny carrot tag — pastel, vintage or kids' style, cut lines and string hole.",
        h1: 'Easter gift tags: egg, bunny and carrot in three styles',
        tagline: 'Nine tags, three shapes, three styles each — cut lines and a string hole on every one.',
        intro: "Three Easter gift tags for the basket: an egg-shaped tag, a from-the-bunny tag, a tiny carrot tag. Every tag is drawn with cut lines and a small string hole, in a soft pastel look, a vintage two-tone finish, or a bright kids' style. Cut, punch, and tie onto a basket handle or gift ribbon. Type your own wording and the set redraws.",
        useCases: [
            'an Easter basket gift tag',
            'a name tag for an egg hunt prize',
            'a handmade gift wrap accent',
            'a classroom Easter party favor tag',
        ],
        faq: [
            {q: 'What size are the tags?', a: 'Each tag is drawn small enough for a basket handle or gift ribbon, with cut lines and a punched-hole mark.'},
            {q: 'What do I need to attach the tag?', a: 'Cut it out, punch the marked hole, and thread ribbon, string, or twine through to tie onto the basket or gift.'},
            {q: 'Can I change the wording?', a: "Yes — type your own message, like a child's name, and the tag redraws in the same style."},
            {q: "Is there a version for a baby's first Easter?", a: "The pastel set leans soft and gentle for exactly that, while the vintage set carries more contrast and the kids' set stays bright."},
        ],
    },
}
